import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import {
  Breadcrumb,
  DetailEmpty,
  DetailField,
  DetailGrid,
  FormSection,
  PageHeader,
  StatusBadge,
} from "../../components/ui";
import { format } from "date-fns";

import {
  useGetBenefitByIdQuery,
  useGetBenefitPartnersQuery,
} from "../../store/services/benefits";
import { useGetTenantsQuery } from "../../store/services/tenants";
import {
  benefitCtaModeOptions,
  resolveBenefitCtaMode,
  benefitValidDayOptions,
  getBenefitDiscountTypeLabel,
  getBenefitRedemptionMethodLabel,
  getBenefitStatusLabel,
  getBenefitStatusTone,
} from "../../utils/constants/benefits-constants";
import { Benefit } from "../../models/benefits";

const parseBenefitDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatBenefitDate = (value: string | null): string | undefined => {
  const date = parseBenefitDate(value);
  return date ? format(date, "dd/MM/yyyy") : undefined;
};

const getSortedImages = (benefit: Benefit) =>
  [...(benefit.images ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );

const getCtaModeLabel = (value?: string | null): string =>
  benefitCtaModeOptions.find((o) => o.value === value)?.label ?? value ?? "-";

const getValidDayLabel = (value: number): string =>
  benefitValidDayOptions.find((o) => o.value === value)?.label ?? String(value);

// Misma regla que el backend: horario vacío = desde 00:00 / hasta 23:59.
const formatValidHours = (start: string | null, end: string | null): string =>
  !start && !end ? "Todo el día" : `${start ?? "00:00"} a ${end ?? "23:59"}`;

export const BenefitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: benefit, isLoading } = useGetBenefitByIdQuery(id || "", {
    skip: !id,
  });
  // El listado no fuerza tenant: se busca el partner/tenant del beneficio en
  // el universo global, igual que hace BenefitListDT.
  const { data: partners } = useGetBenefitPartnersQuery({ tenantId: null });
  const { data: tenants } = useGetTenantsQuery({});

  // cta_mode NULL (beneficios viejos): se deduce igual que en la app.
  const ctaMode = resolveBenefitCtaMode(benefit);

  const partnerName = React.useMemo(() => {
    if (!benefit) return undefined;
    return (partners ?? []).find((p) => p.id === benefit.partnerId)?.name;
  }, [partners, benefit]);

  const tenantName = React.useMemo(() => {
    if (!benefit || !Array.isArray(tenants)) return undefined;
    const tenant = (tenants as any[]).find(
      (t) => String(t.id) === String(benefit.tenantId)
    );
    return tenant?.provider;
  }, [tenants, benefit]);

  if (isLoading) {
    return (
      <div className="gap-4 flex flex-column justify-content-center align-items-center p-3 min-h-11rem p-8">
        <h1 className="text-center text-3xl font-bold mb-6">
          Cargando contenido
        </h1>
        <ProgressSpinner />
      </div>
    );
  }

  if (!benefit) {
    return (
      <div className="p-card p-5 text-center">
        <h2>Beneficio no encontrado</h2>
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          onClick={() => navigate("/benefits-catalog")}
        />
      </div>
    );
  }

  const vigenciaDesde = formatBenefitDate(benefit.startDate);
  const vigenciaHasta = formatBenefitDate(benefit.endDate);
  const sortedImages = getSortedImages(benefit);

  return (
    <>
      <Breadcrumb items={[{ label: "Beneficios", to: "/benefits-catalog" }, { label: benefit.title ?? "Detalle" }]} />
    <div className="p-card gap-4 p-3">
      {/* §6.1 — título centrado; "Editar" es una acción secundaria de página:
          ícono circular contiguo al título. Volver queda en el breadcrumb. */}
      <PageHeader
        title={benefit.title ?? "-"}
        badge={
          <StatusBadge
            label={getBenefitStatusLabel(benefit.status)}
            tone={getBenefitStatusTone(benefit.status)}
          />
        }
        secondaryActions={[
          {
            icon: "pi pi-pencil",
            label: "Editar beneficio",
            filled: true,
            onClick: () => navigate(`/benefits-catalog/${benefit.id}/edit`),
          },
        ]}
      />

      <FormSection title="Información general" defaultOpen>
        <DetailGrid>
          <DetailField label="Partner">{partnerName ?? benefit.partnerId}</DetailField>
          <DetailField label="Tenant">{tenantName ?? benefit.tenantId}</DetailField>
          <DetailField label="Descripción">
            {benefit.description || <DetailEmpty />}
          </DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title="Descuento" defaultOpen>
        <DetailGrid>
          <DetailField label="Tipo">
            {getBenefitDiscountTypeLabel(benefit.discountType)}
          </DetailField>
          <DetailField label="Valor">{benefit.discountValue || <DetailEmpty />}</DetailField>
          <DetailField label="Etiqueta">{benefit.discountLabel || <DetailEmpty />}</DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title="CTA y canje" defaultOpen>
        <DetailGrid>
          {/* Mismos campos que el formulario según el modo: con texto
              informativo la app no muestra botón ni canje. */}
          <DetailField label="Modo de CTA">
            {ctaMode ? getCtaModeLabel(ctaMode) : <DetailEmpty>Sin definir</DetailEmpty>}
          </DetailField>
          {ctaMode === "info_text" ? (
            <DetailField label="Texto informativo" wide>
              {benefit.ctaInfoText || <DetailEmpty />}
            </DetailField>
          ) : (
            <>
              <DetailField label="Texto del botón">
                {benefit.ctaLabel || <DetailEmpty>Usar beneficio (por defecto)</DetailEmpty>}
              </DetailField>
              <DetailField label="Métodos de canje">
                <div className="flex flex-wrap gap-2">
                  {benefit.redemptionMethods?.length ? (
                    benefit.redemptionMethods.map((method) => (
                      <Tag key={method} severity="info" value={getBenefitRedemptionMethodLabel(method)} />
                    ))
                  ) : (
                    <DetailEmpty>Sin métodos definidos</DetailEmpty>
                  )}
                </div>
              </DetailField>
              {benefit.redemptionMethods?.includes("promo_code") && (
                <DetailField label="Código promocional">
                  {benefit.promoCode || <DetailEmpty />}
                </DetailField>
              )}
              {benefit.redemptionImageUrl && (
                <DetailField label="Imagen de canje">
                  <img
                    src={benefit.redemptionImageUrl}
                    alt="Imagen de canje"
                    style={{
                      width: 160,
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 8,
                      display: "block",
                    }}
                  />
                </DetailField>
              )}
            </>
          )}
        </DetailGrid>
      </FormSection>

      <FormSection title="Condiciones y vigencia" defaultOpen>
        <DetailGrid>
          <DetailField label="Inicio de vigencia">
            {vigenciaDesde ?? <DetailEmpty>Sin límite</DetailEmpty>}
          </DetailField>
          <DetailField label="Fin de vigencia">
            {vigenciaHasta ?? <DetailEmpty>Sin límite</DetailEmpty>}
          </DetailField>
          <DetailField label="Términos y condiciones">
            {benefit.termsAndConditions || <DetailEmpty />}
          </DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title="Validez" defaultOpen>
        <DetailGrid>
          <DetailField label="Días de la semana">
            <div className="flex flex-wrap gap-2">
              {benefit.validDays?.length ? (
                benefit.validDays.map((day) => (
                  <Tag key={day} value={getValidDayLabel(day)} severity="info" />
                ))
              ) : (
                <DetailEmpty>Todos los días</DetailEmpty>
              )}
            </div>
          </DetailField>
          <DetailField label="Horario">
            {formatValidHours(benefit.validHoursStart, benefit.validHoursEnd)}
          </DetailField>
          <DetailField label="Roles elegibles">
            <div className="flex flex-wrap gap-2">
              {benefit.eligibleRoles?.length ? (
                benefit.eligibleRoles.map((role) => (
                  <Tag key={role} value={role} severity="info" />
                ))
              ) : (
                <DetailEmpty>Todos</DetailEmpty>
              )}
            </div>
          </DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title={`Imágenes (${sortedImages.length})`} defaultOpen>
        {sortedImages.length ? (
          <div className="flex flex-wrap gap-3">
            {sortedImages.map((image) => (
              <img
                key={image.id}
                src={image.imageUrl ?? undefined}
                alt={benefit.title ?? "Beneficio"}
                style={{
                  width: 120,
                  height: 120,
                  objectFit: "cover",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            ))}
          </div>
        ) : (
          <DetailEmpty>No hay imágenes todavía</DetailEmpty>
        )}
      </FormSection>
    </div>
    </>
  );
};
